import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Info } from 'lucide';
import { api } from '../api.js';
import { EXTRACTION_PROFILES } from '@mv/models';
import { auth } from '../auth.js';
import { lucideIcon } from '../icon.js';
import './instructions-editor.js';

/**
 * Sysadmin page (/admin/recorder): edit the system-wide DEFAULT
 * recording instructions. Every company falls back to this until it saves its
 * own text.
 */
export class AdminRecorderSettings extends LitElement {
  static properties = {
    _loading: { state: true },
    _saving: { state: true },
    _saved: { state: true },
    _error: { state: true },
    _value: { state: true },
    _over: { state: true },
    _profile: { state: true },
    _ready: { state: true },
  };

  constructor() {
    super();
    this._profile = 'nen6693';
    this._loading = true;
    this._saving = false;
    this._saved = false;
    this._error = null;
    this._value = '';
    this._html = '';
    this._over = false;
  }

  connectedCallback() { super.connectedCallback(); if (auth.isSystemAdmin()) this._load(); else this._loading = false; }

  async _load() {
    this._loading = true;
    try {
      const res = await api.getDefaultRecordingInstructions(this._profile);
      this._value = res?.instructions ?? '';
      this._html = this._value;
    } catch (e) {
      this._error = 'Laden mislukt';
    } finally {
      this._loading = false;
      this._ready = true;
    }
  }

  async _switchProfile(id) {
    if (id === this._profile) return;
    this._profile = id;
    this._saved = false; this._error = null; this._over = false;
    await this._load();
  }

  _onChange(e) { this._html = e.detail.html; this._over = e.detail.over; this._saved = false; }

  async _save() {
    this._saving = true; this._saved = false; this._error = null;
    try {
      await api.updateDefaultRecordingInstructions(this._profile, this._html);
      this._saved = true;
      setTimeout(() => { this._saved = false; }, 2500);
    } catch (e) {
      this._error = 'Opslaan mislukt';
    } finally {
      this._saving = false;
    }
  }

  render() {
    if (!auth.isSystemAdmin()) {
      return html`<div class="page"><h1>Geen toegang</h1><p class="subtitle">Deze pagina is alleen voor systeembeheerders.</p></div>`;
    }
    if (!this._ready) return html`<div class="page"><p class="muted">Laden…</p></div>`;
    return html`
      <div class="page">
        <h1>Standaard opname-instructies</h1>
        <p class="subtitle">Voorbeeldtekst per NEN-standaard. Maximaal 100 woorden.</p>

        <div class="info-banner">
          <span class="info-icon">${unsafeSVG(lucideIcon(Info, 18))}</span>
          <span>Dit is de standaardtekst die elk bedrijf automatisch overneemt, per model. Een bedrijf kan deze op zijn eigen instellingenpagina aanpassen, en met "Reset standaardtekst" altijd weer naar deze tekst terugkeren.</span>
        </div>

        <div class="tabs">
          ${EXTRACTION_PROFILES.map((p) => html`
            <button class="tab ${this._profile === p.id ? 'active' : ''}" title=${p.description} @click=${() => this._switchProfile(p.id)}>${p.label}</button>
          `)}
        </div>

        <instructions-editor .value=${this._value} @change=${this._onChange}></instructions-editor>

        <div class="actions">
          ${this._error ? html`<span class="err">${this._error}</span>` : ''}
          ${this._saved ? html`<span class="ok">Opgeslagen ✓</span>` : ''}
          <button class="save" ?disabled=${this._saving || this._over} @click=${this._save}>${this._saving ? 'Opslaan…' : 'Opslaan'}</button>
        </div>
      </div>
    `;
  }

  static styles = css`
    :host { display: block; }
    .page { max-width: 820px; }
    h1 { margin: 0 0 0.35rem; font-size: 1.5rem; font-weight: 700; color: var(--mv-text); }
    .subtitle { color: var(--mv-text-muted); margin: 0 0 1rem; font-size: 0.9rem; }
    .info-banner {
      display: flex; gap: 10px; padding: 12px 14px; border-radius: 10px;
      background: rgba(59,130,246,0.08); border: 1px solid rgba(59,130,246,0.25);
      color: var(--mv-text); margin: 0 0 1.5rem; font-size: 0.88rem;
    }
    .info-icon { flex-shrink: 0; }
    .muted { color: var(--mv-text-muted); }
    .tabs { display: inline-flex; flex-wrap: wrap; gap: 4px; background: var(--mv-surface-alt, #F3F4F6); padding: 4px; border-radius: 10px; margin-bottom: 1rem; }
    .tab {
      background: transparent; border: none; border-radius: 7px; padding: 7px 16px;
      font-weight: 700; font-size: 0.85rem; cursor: pointer; color: var(--mv-text-sub, #6c757d);
    }
    .tab.active { background: var(--mv-surface, #fff); color: var(--mv-primary); box-shadow: 0 1px 2px rgba(0,0,0,0.06); }
    .actions { display: flex; align-items: center; justify-content: flex-end; gap: 14px; margin-top: 1.25rem; }
    .ok { color: var(--mv-primary); font-weight: 600; font-size: 0.9rem; }
    .err { color: #DC2626; font-weight: 600; font-size: 0.9rem; }
    button.save {
      background: var(--mv-primary); color: #fff; border: none; border-radius: 8px;
      padding: 10px 22px; font-weight: 700; font-size: 0.9rem; cursor: pointer;
    }
    button.save:disabled { opacity: 0.55; cursor: default; }
  `;
}

customElements.define('admin-recorder-settings', AdminRecorderSettings);
